// Brokerage / Sub-Broker (PR-3, WS-1) — the firm Production tab. Standalone
// component (WS-D owns wiring it into BrokerageDetailPage); reuses the broker-
// team dashboard's MetricCard KPI row, CSS-bar pipeline mix, and DataTable
// roster. Display / tracking only — the split ledger reflects commissions paid
// to the firm and split with sub-brokers OFF Phoenix's platform.

import { Link } from "react-router-dom";
import type {
  BrokeragePipelineMixDto,
  BrokerageProductionSubBrokerDto,
} from "@/api/brokerages";
import { useBrokerageProduction } from "@/hooks/brokerages/useBrokerageProduction";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { MetricCard } from "@/components/portfolioAdmin/metrics/MetricsCards";
import { Button, Card, Skeleton } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";

const STAGE_LABEL: Record<string, string> = {
  Intake: "Intake",
  PreApproval: "Pre-approval",
  Underwriting: "Underwriting",
  Closing: "Closing",
  Closed: "Closed",
  Declined: "Declined",
  Withdrawn: "Withdrawn",
};

export function ProductionTab({ brokerageId }: { brokerageId: string }) {
  const production = useBrokerageProduction(brokerageId);

  const columns: Column<BrokerageProductionSubBrokerDto>[] = [
    {
      key: "name",
      header: "Sub-broker",
      render: (r) => (
        <Link
          to={`/brokers/${r.subBrokerId}`}
          className="font-semibold hover:text-gold-shadow"
        >
          {r.subBrokerName}
        </Link>
      ),
    },
    {
      key: "deals",
      header: "Deals",
      className: "text-right tabular-nums",
      render: (r) => r.dealCount,
    },
    {
      key: "gross",
      header: "Gross received",
      className: "text-right tabular-nums",
      render: (r) => formatCurrency(r.ledgerGross),
    },
  ];

  if (production.isLoading) {
    return (
      <div className="flex flex-col gap-space-3" data-testid="production-loading">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (production.isError || !production.data) {
    return (
      <Card data-testid="production-error">
        <p className="text-small text-status-error m-0 mb-space-2">
          Couldn't load production for this brokerage.
        </p>
        <Button size="sm" variant="secondary" onClick={() => void production.refetch()}>
          Retry
        </Button>
      </Card>
    );
  }

  const d = production.data;

  return (
    <div className="flex flex-col gap-space-4" data-testid="production-tab">
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-space-3"
        data-testid="production-stat-tiles"
      >
        <MetricCard
          label="Sub-brokers"
          value={d.subBrokerCount}
          testId="production-tile-subbrokers"
        />
        <MetricCard
          label="Deals sourced"
          value={d.dealsSourcedCount}
          caption={`${formatCurrency(d.dealsSourcedVolume)} volume`}
          testId="production-tile-deals"
        />
        <MetricCard
          label="Loans originated"
          value={d.loansOriginatedCount}
          caption={`${formatCurrency(d.loansOriginatedTotal)} total`}
          testId="production-tile-loans"
        />
        <MetricCard
          label="Total gross received"
          value={formatCurrency(d.totalGross)}
          testId="production-tile-gross"
        />
      </div>

      <Card data-testid="production-pipeline-mix">
        <h2 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-2">
          Pipeline mix
        </h2>
        <PipelineMixBars mix={d.pipelineMix} />
      </Card>

      <div className="flex flex-col gap-space-2">
        <h2 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0">
          Sub-broker roster
        </h2>
        <DataTable<BrokerageProductionSubBrokerDto>
          columns={columns}
          rows={d.subBrokers}
          emptyMessage="No active sub-brokers or tracked splits yet."
          rowKey={(r) => r.subBrokerId}
          data-testid="production-roster-table"
        />
      </div>
    </div>
  );
}

/** CSS-only horizontal count bars per pipeline stage — lifted from the broker-
 * team dashboard (no chart lib). */
function PipelineMixBars({ mix }: { mix: BrokeragePipelineMixDto[] }) {
  const max = Math.max(...mix.map((m) => m.count), 0);
  if (max === 0) {
    return (
      <p className="text-small text-gray-mid m-0" data-testid="production-pipeline-mix-empty">
        No deals sourced by this brokerage yet.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-space-1">
      {mix.map((m) => (
        <div
          key={m.stage}
          className="grid grid-cols-[8rem_1fr_2.5rem] items-center gap-space-2"
          data-testid={`production-stage-${m.stage}`}
        >
          <span className="text-caption text-gray-mid uppercase tracking-wide truncate">
            {STAGE_LABEL[m.stage] ?? m.stage}
          </span>
          <span className="h-2 rounded-sm bg-white-warm overflow-hidden">
            <span
              className="block h-full bg-gold rounded-sm"
              style={{ width: `${(m.count / max) * 100}%` }}
              aria-hidden
            />
          </span>
          <span className="text-small font-semibold tabular-nums text-right">{m.count}</span>
        </div>
      ))}
    </div>
  );
}
